import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Gauge, Pencil, Truck, X, ChevronLeft, ChevronRight, ArrowRight, AlertTriangle, Wrench } from "lucide-react";
import { addDays, endOfDay, format, isToday, startOfDay } from "date-fns";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tractor, Location } from "@/types/tms";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { supabase } from "@/integrations/supabase/client";
import {
  ORDERS_WITH_LEGS_SELECT,
  ORDERS_WITH_LEGS_VIEW,
  mapAndHydrateLegRows,
} from "@/lib/orders-with-legs";
import { TractorEligibilityCard } from "@/components/tractors/TractorEligibilityCard";
import {
  getTractorServiceStatus,
  type TractorServiceStatus,
} from "@/lib/tractor-service-status";

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">{value || "—"}</span>
    </div>
  );
}

interface TractorDetailSheetProps {
  tractor: (Tractor & { home_base?: Location | null }) | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (tractor: Tractor) => void;
  odometerMiles?: number;
}

export function TractorDetailSheet({ tractor, open, onOpenChange, onEdit, odometerMiles }: TractorDetailSheetProps) {
  if (!tractor) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="w-full lg:w-[440px] lg:max-w-[440px] max-w-full overflow-y-auto [&>button.absolute]:hidden p-0"
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { onOpenChange(false); onEdit(tractor); }}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="px-4 pb-4">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">{tractor.name}</h2>
            <TractorOosPill tractorId={tractor.id} />
          </div>
        </div>

        <div className="px-4 pb-4 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-0">
              <DetailRow label="Base Truck" value={tractor.base_truck} />
              <DetailRow label="Home Base" value={tractor.home_base?.short_code} />
              <DetailRow label="VIN" value={tractor.vin ? <span className="font-mono text-xs">{tractor.vin}</span> : undefined} />
              <DetailRow label="Samsara ID" value={tractor.samsara_id ? <span className="font-mono text-xs">{tractor.samsara_id}</span> : undefined} />
              <DetailRow
                label="Odometer"
                value={
                  odometerMiles != null ? (
                    <span className="flex items-center gap-1.5">
                      <Gauge className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-mono">{odometerMiles.toLocaleString()}</span>
                      <span className="text-xs text-muted-foreground">mi</span>
                    </span>
                  ) : undefined
                }
              />
            </CardContent>
          </Card>

          <TractorScheduleCard tractorId={tractor.id} />
          <TractorEligibilityCard tractorId={tractor.id} />
          <TractorServiceRecordsCard tractorId={tractor.id} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TractorOosPill({ tractorId }: { tractorId: string }) {
  const { data } = useQuery({
    queryKey: ["tractor-oos-pill", tractorId],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tractor_service_records" as never)
        .select("status, window_start, window_end")
        .eq("tractor_id", tractorId)
        .in("status", ["open", "updated"]);
      if (error) throw error;
      return getTractorServiceStatus(
        (data ?? []) as unknown as {
          status: string;
          window_start: string | null;
          window_end: string | null;
        }[],
      );
    },
  });
  if (!data || data === "available") return null;
  const label: Record<TractorServiceStatus, string> = {
    available: "Available",
    oos_current: "OOS",
    oos_past_due: "OOS past due",
    oos_indefinite: "OOS indefinite",
    upcoming_service: "Upcoming service",
  };
  const tone =
    data === "upcoming_service"
      ? "bg-muted text-muted-foreground"
      : "bg-destructive/15 text-destructive";
  return (
    <span className={`ml-1 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${tone}`}>
      <Wrench className="h-3 w-3" />
      {label[data]}
    </span>
  );
}

interface ServiceRecord {
  id: string;
  status: "open" | "closed" | "updated";
  nature: string | null;
  reason_summary: string | null;
  window_start: string | null;
  window_end: string | null;
  policy_flags: string[] | null;
  source: string;
  created_at: string;
}

function TractorServiceRecordsCard({ tractorId }: { tractorId: string }) {
  const { data: records, isLoading } = useQuery({
    queryKey: ["tractor-service-records", tractorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tractor_service_records" as never)
        .select("id, status, nature, reason_summary, window_start, window_end, policy_flags, source, created_at")
        .eq("tractor_id", tractorId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as ServiceRecord[];
    },
  });

  const openCount = records?.filter((r) => r.status === "open").length ?? 0;

  const statusStyles: Record<ServiceRecord["status"], string> = {
    open: "bg-destructive/15 text-destructive",
    closed: "bg-muted text-muted-foreground",
    updated: "bg-primary/15 text-primary",
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm flex items-center gap-1.5">
          <Wrench className="h-3.5 w-3.5" />
          Service Records
        </CardTitle>
        {openCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-md bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">
            <AlertTriangle className="h-3 w-3" />
            {openCount} open
          </span>
        )}
      </CardHeader>
      <CardContent className="pt-2">
        {isLoading ? (
          <p className="text-xs text-muted-foreground py-4 text-center">Loading…</p>
        ) : !records || records.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">No service records</p>
        ) : (
          <div className="space-y-2">
            {records.map((r) => (
              <div key={r.id} className="rounded-md border p-2 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${statusStyles[r.status]}`}>
                    {r.status}
                  </span>
                  <span className="text-[11px] text-muted-foreground font-mono">
                    {format(new Date(r.created_at), "MMM d, HH:mm")}
                  </span>
                </div>
                {r.nature && <div className="text-xs font-medium">{r.nature}</div>}
                {r.reason_summary && (
                  <div className="text-xs text-muted-foreground">{r.reason_summary}</div>
                )}
                {(r.window_start || r.window_end) && (
                  <div className="text-[11px] text-muted-foreground font-mono">
                    {r.window_start ? format(new Date(r.window_start), "MMM d HH:mm") : "—"}
                    {" → "}
                    {r.window_end ? format(new Date(r.window_end), "MMM d HH:mm") : "—"}
                  </div>
                )}
                {r.policy_flags && r.policy_flags.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {r.policy_flags.map((f) => (
                      <span key={f} className="text-[10px] rounded bg-muted px-1.5 py-0.5">{f}</span>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{r.source}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TractorScheduleCard({ tractorId }: { tractorId: string }) {
  const navigate = useNavigate();
  const [day, setDay] = useState<Date>(() => startOfDay(new Date()));
  const dayStart = startOfDay(day);
  const dayEnd = endOfDay(day);

  const { data: orders, isLoading } = useQuery({
    queryKey: ["tractor-day-orders", tractorId, dayStart.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDERS_WITH_LEGS_SELECT)
        .eq("assigned_tractor_id", tractorId)
        .gte("ready_time", dayStart.toISOString())
        .lte("ready_time", dayEnd.toISOString())
        .order("ready_time");
      if (error) throw error;
      return mapAndHydrateLegRows(data);
    },
  });

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">Schedule</CardTitle>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setDay((d) => addDays(d, -1))}
            aria-label="Previous day"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs font-medium min-w-[90px] text-center">
            {isToday(day) ? "Today" : format(day, "EEE, MMM d")}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setDay((d) => addDays(d, 1))}
            aria-label="Next day"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {isLoading ? (
          <p className="text-xs text-muted-foreground py-4 text-center">Loading…</p>
        ) : !orders || orders.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">
            No assignments on {format(day, "MMM d")}
          </p>
        ) : (
          <div className="space-y-2">
            {orders.map((o: any) => {
              const targetId = o.parent_order_id ?? o.id;
              const legParam = o.parent_order_id ? `?leg=${o.id}` : "";
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => navigate(`/orders/${targetId}${legParam}`)}
                  className="w-full text-left rounded-md border p-2 hover:bg-accent transition-colors flex flex-col gap-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-semibold truncate">{o.unique_id}</span>
                    <OrderStatusBadge status={o.status} />
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="truncate">{o.ship_from_city}</span>
                    <ArrowRight className="h-3 w-3 shrink-0" />
                    <span className="truncate">{o.ship_to_city}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono">
                    {format(new Date(o.ready_time), "HH:mm")}
                    {" → "}
                    {format(new Date(o.requested_delivery_time_end ?? o.requested_delivery_time), "HH:mm")}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
